import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

// Role definitions matching backend
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  LOP_MANAGER: 'lop_manager',
  PAYROLL_AUDITOR: 'payroll_auditor',
  ACCOUNT_EXECUTIVE: 'account_executive',
};

// Legacy role mapping
const LEGACY_ROLES = {
  manager: ROLES.LOP_MANAGER,
  accountant: ROLES.ACCOUNT_EXECUTIVE,
};

// Role display info
export const ROLE_INFO = {
  [ROLES.SUPER_ADMIN]: {
    label: 'Super Admin',
    description: 'Full system access — can edit salary entries even after finalization',
    color: 'red',
  },
  [ROLES.ADMIN]: {
    label: 'Admin',
    description: 'Full system access with no restrictions (except finalized records)',
    color: 'purple',
  },
  [ROLES.LOP_MANAGER]: {
    label: 'LOP Manager',
    description: 'Can manage employees and enter LOP values only',
    color: 'blue',
  },
  [ROLES.PAYROLL_AUDITOR]: {
    label: 'Payroll Auditor',
    description: 'Full payroll access with edit capabilities',
    color: 'amber',
  },
  [ROLES.ACCOUNT_EXECUTIVE]: {
    label: 'Account Executive',
    description: 'View access and payment marking only',
    color: 'green',
  },
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Normalize role for backward compatibility
  const normalizeRole = (role) => LEGACY_ROLES[role] || role;

  useEffect(() => {
    const token = localStorage.getItem('paytrack_token');
    const savedUser = localStorage.getItem('paytrack_user');
    
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      parsedUser.role = normalizeRole(parsedUser.role);
      setUser(parsedUser);
      
      // Verify token is still valid
      authAPI.me()
        .then((res) => {
          const userData = res.data;
          userData.role = normalizeRole(userData.role);
          setUser(userData);
          localStorage.setItem('paytrack_user', JSON.stringify(userData));
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    userData.role = normalizeRole(userData.role);
    
    localStorage.setItem('paytrack_token', access_token);
    localStorage.setItem('paytrack_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    const { access_token, user: newUser } = response.data;
    newUser.role = normalizeRole(newUser.role);
    
    localStorage.setItem('paytrack_token', access_token);
    localStorage.setItem('paytrack_user', JSON.stringify(newUser));
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('paytrack_token');
    localStorage.removeItem('paytrack_user');
    setUser(null);
  };

  // Permission helper functions
  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(p => user?.permissions?.includes(p));
  };

  // Role checkers
  const isSuperAdmin = () => user?.role === ROLES.SUPER_ADMIN;
  const isAdmin = () => user?.role === ROLES.ADMIN;
  const isLOPManager = () => user?.role === ROLES.LOP_MANAGER;
  const isPayrollAuditor = () => user?.role === ROLES.PAYROLL_AUDITOR;
  const isAccountExecutive = () => user?.role === ROLES.ACCOUNT_EXECUTIVE;
  
  // Helper: is admin-level (Super Admin or Admin)
  const isAdminLevel = () => isSuperAdmin() || isAdmin();

  // Permission-based access control
  const canViewSalary = () => hasPermission('employee:salary_view');
  const canEditSalary = () => hasPermission('employee:salary_edit');
  const canEditLOP = () => hasPermission('lop:edit');
  const canViewPayroll = () => hasPermission('payroll:view');
  const canEditPayroll = () => hasPermission('payroll:edit');
  const canFinalize = () => hasPermission('payroll:finalize');
  const canMarkPaid = () => hasPermission('payroll:mark_paid');
  const canReversePayment = () => hasPermission('payroll:reverse');
  const canEditFinalized = () => hasPermission('payroll:edit_finalized');
  const canManageUsers = () => hasPermission('user:create');
  const canViewAudit = () => hasPermission('audit:view');
  const canViewReports = () => hasPermission('report:view');
  const canExportReports = () => hasPermission('report:export');
  const canManageOrgs = () => hasPermission('org:create');
  const canManageVerticals = () => hasPermission('vertical:manage');
  const canManageEmployees = () => hasPermission('employee:create');
  const canDeleteEmployees = () => hasPermission('employee:delete');
  const canManageIncrements = () => hasPermission('increment:create');
  const canViewSettings = () => hasPermission('settings:view');
  const canEditSettings = () => hasPermission('settings:edit');

  // UI visibility helpers
  const shouldHideSalaryFields = () => !canViewSalary();
  const shouldShowLOPOnly = () => isLOPManager();

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      // Role checkers
      isSuperAdmin,
      isAdmin,
      isAdminLevel,
      isLOPManager,
      isPayrollAuditor,
      isAccountExecutive,
      // Permission checkers
      hasPermission,
      hasAnyPermission,
      canViewSalary,
      canEditSalary,
      canEditLOP,
      canViewPayroll,
      canEditPayroll,
      canFinalize,
      canMarkPaid,
      canReversePayment,
      canEditFinalized,
      canManageUsers,
      canViewAudit,
      canViewReports,
      canExportReports,
      canManageOrgs,
      canManageVerticals,
      canManageEmployees,
      canDeleteEmployees,
      canManageIncrements,
      canViewSettings,
      canEditSettings,
      // UI helpers
      shouldHideSalaryFields,
      shouldShowLOPOnly,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
